"use client"

import React, { useState } from "react"
import { useRouter, useSearchParams } from "next/navigation"

import { TResolvedNewsSubpageItem } from "@/lib/kontent/pages/getNewsAggregator"
import { cn } from "@/lib/utils"
import {
  CardNewsResolver,
  ChipFilterListResolver,
} from "@/components/ui/molecules/resolvers"
import { Pagination, TPagination } from "@/components/ui/molecules"
import { CardListContainer } from "@/components/ui/organisms"

type ArticleListTemplateProps = {
  pageUrl: string
  codename: string
  totalPageCount: number
  initialArticles: TResolvedNewsSubpageItem[]
  filters: Array<{ codename: string; label: string }>
  preview?: boolean
}

const ArticleListTemplate = ({
  pageUrl,
  codename,
  totalPageCount: _totalPageCount,
  initialArticles,
  filters,
  preview = false,
}: ArticleListTemplateProps) => {
  const searchParams = useSearchParams()
  const router = useRouter()

  // In order make pagination work with preview mode, we need to change the pageUrl to the codename
  let baseUrl = pageUrl
  if (preview) {
    baseUrl = codename
  }

  // Read params from URL
  const currentPageParam = searchParams.get("page")
  const currentPage = currentPageParam ? parseInt(currentPageParam, 10) : 1
  const filterParam = searchParams.get("filter")

  const limitNews = 9

  // Initialize selectedFilter from URL param if present
  const [selectedFilter, setSelectedFilterState] = useState<string | null>(
    filterParam ?? null
  )
  const [initialized, setInitialized] = useState(false)
  if (!initialized) {
    setSelectedFilterState(filterParam ?? null)
    setInitialized(true)
  }

  // Handle filter change: update state and URL, reset to page 1
  const handleFilterChange = (newFilter: string | null) => {
    setSelectedFilterState(newFilter)
    const params = new URLSearchParams()
    if (newFilter) {
      params.set("filter", newFilter)
    }
    params.set("page", "1")
    router.push(`${baseUrl}?${params.toString()}`, { scroll: false })
  }

  // Apply filter client-side from the full initial list
  const filteredArticles = (initialArticles ?? []).filter((article) => {
    if (!selectedFilter) return true
    return article.theme?.codename === selectedFilter
  })

  // Pagination calculations based on filtered list
  const totalNewsCount = filteredArticles.length
  const pageCount = Math.max(1, Math.ceil(totalNewsCount / limitNews))
  const currentPageClamped = Math.min(Math.max(currentPage || 1, 1), pageCount)
  const offset = (currentPageClamped - 1) * limitNews
  const articlesToShow = filteredArticles.slice(offset, offset + limitNews)

  const showPagination = totalNewsCount > limitNews

  // Build pagination list preserving active filter param
  const paginationList: TPagination[] = []
  for (let i = 1; i <= pageCount; i++) {
    const params = new URLSearchParams()
    params.set("page", String(i))
    if (selectedFilter) {
      params.set("filter", selectedFilter)
    }
    paginationList.push({
      label: i,
      isCurrent: i === currentPageClamped,
      url: `${baseUrl}?${params.toString()}`,
    })
  }

  return (
    <>
      <ChipFilterListResolver
        list={filters}
        onFilterChange={handleFilterChange}
      />
      <CardListContainer className={cn(!showPagination ? "pb-12" : null)}>
        {articlesToShow.map((news) => (
          <CardNewsResolver
            key={news.id}
            resolvedData={news}
            isH3={false}
            preview={preview}
          />
        ))}
      </CardListContainer>
      {showPagination ? (
        <footer className="flex items-center justify-center pt-12">
          <Pagination list={paginationList} />
        </footer>
      ) : null}
    </>
  )
}

export { ArticleListTemplate }
